import { z } from "zod";
import { LanguageSchema } from "./schemas";
import { StemModelSchema } from "./stem";
import { AlignmentModel } from "./enums";
import { TaskTypeSchema } from "./shared-schemas";

export const SongTitleAndArtistsSchema = z.object({
  title: z.string(),
  artists: z.array(z.string()),
});

export const AlignmentModelSchema = z.enum([
  AlignmentModel.AudioshakeAlignment,
  AlignmentModel.AudioshakeTranscription,
]);

const AudioSourceTypeSchema = z.enum(["url", "video", "audio", "karafun"]);
const VideoSourceTypeSchema = z.enum([
  "auto",
  "url",
  "video",
  "image",
  "color",
  "karafun",
]);
export const ThumbnailSourceTypeSchema = z.enum(["auto", "image"]);

export const ResolutionSchema = z.object({
  width: z.number(),
  height: z.number(),
});

export const AspectRatioSchema = z.object({
  width: z.number(),
  height: z.number(),
});

export const KarafunFileSchema = z.object({
  type: z.enum(["audio", "video", "metadata"]),
  filepath: z.string(),
  ext: z.string(),
  name: z.string(),
});
export const KarafunExtractResultSchema = z.object({
  dir: z.string(),
  files: z.array(KarafunFileSchema),
});

export const VideoSourceBaseSchema = z.object({
  type: VideoSourceTypeSchema,
});

export const AudioSourceBaseSchema = z.object({
  type: AudioSourceTypeSchema,
  duration: z.number().max(900),
  // title: z.string().min(2),
});

export const ThumbnailSourceBaseSchema = z.object({
  type: ThumbnailSourceTypeSchema,
});

export const ThumbnailSourceImageSchema = ThumbnailSourceBaseSchema.extend({
  type: z.literal("image"),
  url: z.string(),
});

export const ThumbnailSourceAutoSchema = ThumbnailSourceBaseSchema.extend({
  type: z.literal("auto"),
});

export const VideoSourceAutoSchema = VideoSourceBaseSchema.extend({
  type: z.literal("auto"),
});

export const ThumbnailSourceSchema = z.discriminatedUnion("type", [
  ThumbnailSourceImageSchema,
  ThumbnailSourceAutoSchema,
]);

export const VideoSourceUrlSchema = VideoSourceBaseSchema.extend({
  type: z.literal("url"),
  id: z.string().length(11),
  url: z.string().url(),
  aspectRatio: AspectRatioSchema,
});

export const VideoSourceVideoSchema = VideoSourceBaseSchema.extend({
  type: z.literal("video"),
  url: z.string().url(),
  duration: z.number().max(900),
  aspectRatio: AspectRatioSchema,
  resolution: ResolutionSchema.optional(),
});

export const VideoSourceImageSchema = VideoSourceBaseSchema.extend({
  type: z.literal("image"),
  url: z.string().url(),
  size: z.number().max(4 * 1024 * 1024, "Image size must be less than 4MB"),
  aspectRatio: AspectRatioSchema,
  resolution: ResolutionSchema.optional(),
  duration: z.number().min(10).max(600),
  fps: z.number().max(60),
});

export const VideoSourceColorSchema = VideoSourceBaseSchema.extend({
  type: z.literal("color"),
  color: z.string(),
  resolution: ResolutionSchema,
  duration: z.number().min(10).max(600),
  fps: z.number().max(60),
});

export const VideoSourceKarafunSchema = VideoSourceBaseSchema.extend({
  type: z.literal("karafun"),
  filepath: z.string(),
  extractResult: KarafunExtractResultSchema,
});

export const VideoSourceSchema = z.discriminatedUnion("type", [
  // VideoSourceAutoSchema,
  VideoSourceVideoSchema,
  VideoSourceImageSchema,
  VideoSourceColorSchema,
  //   VideoSourceUrlSchema,
  //   VideoSourceKarafunSchema,
]);

export const AudioSourceAudioSchema = AudioSourceBaseSchema.extend({
  type: z.literal("audio"),
  url: z.string().url(),
  model: StemModelSchema,
});

export const AudioSourceVideoSchema = AudioSourceBaseSchema.extend({
  type: z.literal("video"),
  url: z.string().url(),
  model: StemModelSchema,
});

export const AudioSourceKarafunSchema = AudioSourceBaseSchema.extend({
  type: z.literal("karafun"),
  url: z.string().url(),
  extractResult: KarafunExtractResultSchema,
});

export const AudioSourceUrlSchema = AudioSourceBaseSchema.extend({
  type: z.literal("url"),
  id: z.string().length(11),
  url: z.string().url(),
  thumbnail: z.string().url(),
});

export const AudioSourceSchema = z.discriminatedUnion("type", [
  AudioSourceAudioSchema,
  AudioSourceVideoSchema,
  //   AudioSourceUrlSchema,
  //   AudioSourceKarafunSchema,
]);

export const SubtitlesSourceTypeSchema = z.enum([
  "auto",
  "lyrics",
  "lrc",
  "id3",
  "karafun",
  "transcription",
  "alignment",
]);
export const SubtitlesSourceBaseSchema = z.object({
  type: SubtitlesSourceTypeSchema,
});

export const SubtitlesSourceTranscriptionSchema =
  SubtitlesSourceBaseSchema.extend({
    type: z.literal("transcription"),
    model: z.literal(AlignmentModel.AudioshakeTranscription),
    language: LanguageSchema.optional(),
    url: z.string().url(),
  });

export const SubtitlesSourceAlignmentSchema = SubtitlesSourceBaseSchema.extend({
  type: z.literal("alignment"),
  model: z.literal(AlignmentModel.AudioshakeAlignment),
  lyrics: z.string().min(10),
  language: LanguageSchema.optional(),
  url: z.string().url(),
});

export const SubtitlesSourceId3Schema = SubtitlesSourceBaseSchema.extend({
  type: z.literal("id3"),
  url: z.string().url(),
});
export const SubtitlesSourceLrcSchema = SubtitlesSourceBaseSchema.extend({
  type: z.literal("lrc"),
  url: z.string().url(),
});
export const SubtitlesSourceAutoSchema = SubtitlesSourceBaseSchema.extend({
  type: z.literal("auto"),
});
export const SubtitlesSourceKarafunSchema = SubtitlesSourceBaseSchema.extend({
  type: z.literal("karafun"),
  url: z.string().url(),
  extractResult: KarafunExtractResultSchema,
});

export const SubtitlesSourceSchema = z.discriminatedUnion("type", [
  // SubtitlesSourceAutoSchema,
  // SubtitlesSourceLrcSchema,
  // SubtitlesSourceId3Schema,
  // SubtitlesSourceKarafunSchema,
  SubtitlesSourceTranscriptionSchema,
  SubtitlesSourceAlignmentSchema,
]);

export const CreateProjectSchema = z.object({
  title: z.string().min(2),
  lyrics: z.string().optional(),
  language: LanguageSchema.optional(),
  audioSource: AudioSourceSchema,
  videoSource: VideoSourceSchema,
  subtitlesSource: SubtitlesSourceSchema,
});

export const CreateDesktopTaskSchema = z.object({
  desktopId: z.string(),
  title: z.string().min(2),
  artists: z.array(z.string()),
  lyrics: z.string().optional(),
  language: LanguageSchema.optional(),
  taskType: TaskTypeSchema,
  credits: z.number(),
});

export type VideoSource = z.infer<typeof VideoSourceSchema>;
export type VideoSourceType = z.infer<typeof VideoSourceTypeSchema>;
export type VideoSourceImage = z.infer<typeof VideoSourceImageSchema>;
export type VideoSourceColor = z.infer<typeof VideoSourceColorSchema>;
export type VideoSourceVideo = z.infer<typeof VideoSourceVideoSchema>;
export type VideoSourceUrl = z.infer<typeof VideoSourceUrlSchema>;
export type VideoSourceKarafun = z.infer<typeof VideoSourceKarafunSchema>;
export type AudioSource = z.infer<typeof AudioSourceSchema>;
export type AudioSourceType = z.infer<typeof AudioSourceTypeSchema>;
export type AudioSourceAudio = z.infer<typeof AudioSourceAudioSchema>;
export type AudioSourceVideo = z.infer<typeof AudioSourceVideoSchema>;
export type AudioSourceUrl = z.infer<typeof AudioSourceUrlSchema>;
export type AudioSourceKarafun = z.infer<typeof AudioSourceKarafunSchema>;

export type ThumbnailSource = z.infer<typeof ThumbnailSourceSchema>;
export type ThumbnailSourceType = z.infer<typeof ThumbnailSourceTypeSchema>;
export type ThumbnailSourceImage = z.infer<typeof ThumbnailSourceImageSchema>;
export type ThumbnailSourceAuto = z.infer<typeof ThumbnailSourceAutoSchema>;

export type SubtitlesSourceType = z.infer<typeof SubtitlesSourceTypeSchema>;

export type SubtitlesSourceLrc = z.infer<typeof SubtitlesSourceLrcSchema>;
export type SubtitlesSourceAuto = z.infer<typeof SubtitlesSourceAutoSchema>;
export type SubtitlesSourceKarafun = z.infer<
  typeof SubtitlesSourceKarafunSchema
>;
export type SubtitlesSourceId3 = z.infer<typeof SubtitlesSourceId3Schema>;
export type SubtitlesSource = z.infer<typeof SubtitlesSourceSchema>;

export type CreateProjectInput = z.infer<typeof CreateProjectSchema>;

export type SubtitlesSourceTranscription = z.infer<
  typeof SubtitlesSourceTranscriptionSchema
>;
export type SubtitlesSourceAlignment = z.infer<
  typeof SubtitlesSourceAlignmentSchema
>;

export type CreateDesktopTaskInput = z.infer<typeof CreateDesktopTaskSchema>;
